import { API, instance } from "@/services/API";

// OpenAPI `TrailerType` → display label used across the truck list table.
const EQUIPMENT_LABEL = {
  dry_van: "Van",
  reefer: "Reefer",
  flatbed: "Flatbed",
};

// UI sort keys → OpenAPI `PublicTruckListSortField` values.
const SORT_FIELD = {
  age: "age",
  truckId: "truck-id",
  equipment: "equipment",
  location: "location",
  destination: "destination",
  readyDate: "ready-date",
  dispatcher: "dispatcher",
  distance: "distance",
};

// Maps an OpenAPI `PublicTruckListEntry` to the shape the table components consume.
// Decoupling the wire format from the view layer keeps the rows agnostic of the API
// and lets server changes land in one place.
const mapEntry = (entry) => ({
  id: String(entry.id),
  truckNumber: entry.truckNumber ?? "",
  driverName: entry.driverName ?? "",
  equipment: { type: EQUIPMENT_LABEL[entry.equipment] ?? entry.equipment, team: false },
  location: entry.location ?? null,
  preferredDestination: entry.destination ?? null,
  readyDate: entry.readyDate ? String(entry.readyDate).slice(0, 10) : null,
  postedAt: entry.postedAt ?? null,
  dispatcher: { name: entry.dispatcherName ?? "" },
  contact: { email: entry.dispatcherEmail ?? "", phone: entry.dispatcherPhone ?? "" },
  note: entry.note ?? "",
  // Set only while a location search names a place; `approximate` marks straight-line miles.
  distance: entry.distanceMiles != null ? { miles: entry.distanceMiles, approximate: Boolean(entry.distanceApproximate) } : null,
});

// Drops any `null`, `undefined`, or empty-string values so axios doesn't serialize
// e.g. `?dispatcher=` when no dispatcher is selected.
const cleanParams = (params) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    out[key] = value;
  });
  return out;
};

// Public, paginated truck list for a carrier identified by `slug`. The backend
// filters, sorts and paginates server-side (see `/api/v0/public/truck-list/{slug}`
// in `openapi/v0.yaml`); we forward the URL-driven view state straight through.
export const fetchTruckList = async (slug, params = {}) => {
  const { location, dispatcher, equipment, readyDateFrom, readyDateTo, sortKey, sortDirection, page, pageSize } = params;

  const query = cleanParams({
    location,
    dispatcher: dispatcher === "all" ? undefined : dispatcher,
    equipment: equipment === "all" ? undefined : equipment,
    "ready-date-from": readyDateFrom,
    "ready-date-to": readyDateTo,
    "sort-by": SORT_FIELD[sortKey],
    "sort-direction": sortDirection,
    page,
    "page-size": pageSize,
  });

  const data = await API.get(`/public/truck-list/${encodeURIComponent(slug)}`, { params: query });

  return {
    carrier: { name: data.companyName },
    trucks: (data.entries ?? []).map(mapEntry),
    totalCount: data.totalCount ?? 0,
    page: data.page ?? 1,
    pageSize: data.pageSize ?? 0,
    searchLocation: data.searchLocation ?? null,
  };
};

// Dispatchers with at least one truck for the company addressed by `slug`. The
// scope is the whole truck set — independent of the current filter / sort / page,
// so the dropdown stays stable as the user navigates.
export const fetchTruckListDispatchers = (slug) => API.get(`/public/truck-list/${encodeURIComponent(slug)}/dispatchers`);

// Current truck-list opt-in state for the caller's own company. 404 currently
// covers two cases — caller has no company in backoffice, AND no config row
// exists yet — so we collapse both into `null` ("not enabled / not configured")
// and let the toggle render as off. The PUT remains attempt-able from that
// state, so testing the enable flow works even when GET 404s.
//
// Uses `instance` directly so we can read the 404 status; the shared `API.get`
// wrapper flattens errors to strings before they reach us.
export const fetchTruckListConfig = async () => {
  try {
    const response = await instance.get("/truck-list-config");
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

// Toggles the truck-list opt-in for the caller's own company. Returns the new
// config including `slug` so the UI can show the shareable URL once enabled.
export const updateTruckListConfig = (enabled) => API.put("/truck-list-config", { enabled });
